import { useEffect, useState, useCallback } from 'react';
import key from 'weak-key';
import { Button } from '@geberit/gdds';

// styles
import styles from './sidebar.module.scss';

// types
import type { Category, SidebarProps } from '.';

// components
import SidebarItem from './sidebar-item';
import LoadMoreButton from './load-more-button';

// utils
import { calculatePages } from 'utils/locator';
import { classNameBuilder } from 'utils/classNameBuilder';
import { isEmpty } from 'utils/is-empty';
import { PAGE_SIZE } from '../constants';

export default function Sidebar({
  mode,
  showDistance,
  filteredData,
  categories = [],
  sidebarOpen,
  setSidebarOpen,
  handleDetail,
}: Readonly<SidebarProps>) {
  const [remainingPages, setRemainingPages] = useState<number>(
    calculatePages(filteredData, PAGE_SIZE) - 1,
  );
  const [pagedData, setPagedData] = useState(
    remainingPages > 0 ? filteredData.slice(0, PAGE_SIZE) : filteredData,
  );

  useEffect(() => {
    const remaining = calculatePages(filteredData, PAGE_SIZE) - 1;
    setRemainingPages(remaining);
    setPagedData(remaining > 0 ? filteredData.slice(0, PAGE_SIZE) : filteredData);
  }, [filteredData]);

  const retrieveColor = useCallback(
    (itemCategory: string) => {
      if (isEmpty(categories)) return null;
      const category = categories.find(
        (entry) => (entry as Category).categoryName === itemCategory,
      );
      return (category as Category)?.color;
    },
    [categories],
  );

  const nextPage = useCallback(() => {
    setRemainingPages(remainingPages - 1);
    setPagedData([
      ...pagedData,
      ...filteredData.slice(pagedData.length, pagedData.length + PAGE_SIZE),
    ]);
  }, [filteredData, pagedData, remainingPages]);

  return (
    <div className={`${styles.sidebarWrapper} ${!sidebarOpen ? styles.sidebarClosed : ''}`}>
      <div className={classNameBuilder(styles.sidebarToggleButton, styles.nord)}>
        <Button
          className={styles.nord}
          stylingType="primary"
          symbol={sidebarOpen ? 'chevron_right' : 'chevron_left'}
          onClick={() => setSidebarOpen(!sidebarOpen)}
          isIcon
        />
      </div>
      <div className={styles.listWrapper}>
        {!isEmpty(pagedData) &&
          pagedData.map((item) => (
            <SidebarItem
              retrieveColor={retrieveColor}
              mode={mode}
              item={item}
              showDistance={showDistance}
              key={key(item)}
              handleDetail={handleDetail}
            />
          ))}

        {remainingPages > 0 && <LoadMoreButton handleLoadMore={nextPage} />}
      </div>
    </div>
  );
}
